import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchSportsVodLibrary,
  fetchSportsVodSports,
  loadMoreSportsVod,
  setQueryFilter,
  setSourceFilter,
  setSportsFilter,
  toggleSourceFilter,
  toggleSportFilter,
} from '@/store/slices/sportsVodSlice';
import { SportsVodCard } from '@/components/SportsVodCard';
import { SportsVodFilters } from '@/components/SportsVodFilters';
import {
  SPORTS_VOD_SOURCES,
  isKnownSportsVodSource,
  type SportsVodSource,
} from '@/types/sportsVod';

const SOURCE_SET = new Set<SportsVodSource>(SPORTS_VOD_SOURCES);

/** Split a comma-separated URL param into a list of trimmed, non-empty values. */
function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
}

function parseSources(raw: string | null): SportsVodSource[] {
  return parseCsv(raw).filter(isKnownSportsVodSource);
}

function buildSearchParams(sports: string[], sources: SportsVodSource[], q: string): URLSearchParams {
  const params = new URLSearchParams();
  if (sports.length > 0) params.set('sport', sports.join(','));
  if (sources.length > 0) params.set('source', sources.join(','));
  if (q.length > 0) params.set('q', q);
  return params;
}

export function SportsVodLibrary(): JSX.Element {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const titles = useAppSelector((s) => s.sportsVod.titles);
  const sports = useAppSelector((s) => s.sportsVod.sports);
  const status = useAppSelector((s) => s.sportsVod.status);
  const error = useAppSelector((s) => s.sportsVod.error);
  const filters = useAppSelector((s) => s.sportsVod.filters);
  const hasMore = useAppSelector((s) => s.sportsVod.hasMore);
  const loadingMore = useAppSelector((s) => s.sportsVod.loadingMore);

  const urlSports = useMemo(() => parseCsv(searchParams.get('sport')), [searchParams]);
  const urlSources = useMemo(() => parseSources(searchParams.get('source')), [searchParams]);
  const urlQuery = useMemo(() => (searchParams.get('q') ?? '').trim(), [searchParams]);

  // URL is the source of truth for filters — when it changes (back/forward,
  // shared link, programmatic push) we sync the slice to it, then trigger a
  // refetch. The slice owns the request/cursor lifecycle.
  useEffect(() => {
    dispatch(setSportsFilter(urlSports));
    dispatch(setSourceFilter(urlSources));
    dispatch(setQueryFilter(urlQuery));
    void dispatch(fetchSportsVodLibrary());
  }, [dispatch, urlSports.join(','), urlSources.join(','), urlQuery]);

  // Sport facet list — loaded once. Cheap groupBy on the server.
  useEffect(() => {
    void dispatch(fetchSportsVodSports());
  }, [dispatch]);

  function updateUrl(next: { sports?: string[]; sources?: SportsVodSource[]; q?: string }): void {
    const nextSports = next.sports ?? filters.sports;
    const nextSources = next.sources ?? filters.sources;
    const nextQ = next.q ?? filters.q;
    setSearchParams(buildSearchParams(nextSports, nextSources, nextQ), { replace: true });
  }

  function onToggleSport(id: string): void {
    const present = filters.sports.includes(id);
    const nextSports = present ? filters.sports.filter((s) => s !== id) : [...filters.sports, id];
    dispatch(toggleSportFilter(id));
    updateUrl({ sports: nextSports });
  }

  function onToggleSource(id: SportsVodSource): void {
    if (!SOURCE_SET.has(id)) return;
    const present = filters.sources.includes(id);
    const nextSources = present ? filters.sources.filter((s) => s !== id) : [...filters.sources, id];
    dispatch(toggleSourceFilter(id));
    updateUrl({ sources: nextSources });
  }

  function onQueryChange(q: string): void {
    dispatch(setQueryFilter(q));
    updateUrl({ q });
  }

  function onLoadMore(): void {
    void dispatch(loadMoreSportsVod());
  }

  const isInitialLoading = status === 'loading' && titles.length === 0;
  const isEmpty = status === 'ready' && titles.length === 0;

  return (
    <main className="sports-vod-page">
      <header className="sports-vod-page__header">
        <h1>{t('sportsVod.title')}</h1>
      </header>

      <SportsVodFilters
        sports={sports}
        selectedSports={filters.sports}
        selectedSources={filters.sources}
        query={filters.q}
        onToggleSport={onToggleSport}
        onToggleSource={onToggleSource}
        onQueryChange={onQueryChange}
      />

      {error && (
        <div role="alert" className="sports-vod-page__error">
          {error}{' '}
          <button type="button" onClick={() => dispatch(fetchSportsVodLibrary())}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {isInitialLoading && (
        <div className="sports-vod-page__loading" role="status">
          {t('sportsVod.loadingLibrary')}
        </div>
      )}

      {isEmpty && (
        <div className="sports-vod-page__empty">
          {filters.q || filters.sports.length > 0 || filters.sources.length > 0
            ? t('sportsVod.emptyFilters')
            : t('sportsVod.emptyCatalog')}
        </div>
      )}

      {titles.length > 0 && (
        <div className="sports-vod-page__grid" data-testid="sports-vod-grid">
          {titles.map((t) => (
            <SportsVodCard key={t.id} title={t} />
          ))}
        </div>
      )}

      {hasMore && (
        <div className="sports-vod-page__load-more">
          <button
            type="button"
            className="btn"
            onClick={onLoadMore}
            disabled={loadingMore}
            data-testid="sports-vod-load-more"
          >
            {loadingMore ? t('common.loading') : t('sportsVod.loadMore')}
          </button>
        </div>
      )}
    </main>
  );
}
